import { ILanguages, IListingPhotos, IObjectsHasTags, IVirtualTours } from '@llws/lift-entity-interfaces';
import { IApiCommandResponse } from '@llws/api-common';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import { IApiResponse, IEditImage, IModifyPayload, QueryStringCommandOptions } from '@/shared';
import { StoreBaseImplementation } from '@/shared/classes/store-base-implementation';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { INewTranslationSettingsStoreConfig } from '@/shared/interfaces/new-translation-settings';
import { getByModelAndObjectId } from '@/shared/services/tags';
import uploads from '@/shared/services/uploads';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IFilterPayloadV2 } from '@/shared/interfaces/filter-payload-v2';

export interface IListingVirtualToursWithExtraData extends IVirtualTours {
  languages: ILanguages[];
  objectsHasTags: IObjectsHasTags[];
}

export interface ICustomListingVirtualTourState extends IStateStrict<IVirtualTours> {
  singleWithExtraData: IListingVirtualToursWithExtraData | null;
}

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/properties/:param/virtual-tours';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = {
  limit: 999,
  orderBy: [{ field: 'orderBy', direction: 'asc' }],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

const translationSettings: INewTranslationSettingsStoreConfig = {
  model: 'virtual_tours',
  keys: ['title', 'description'],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IListingPhotos>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
  translationSettings,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'listing-videos/'

  state: {
    ...base.state,
    singleWithExtraData: null,
  },

  getters: {
    ...base.getters,
    singleWithExtraData: (state: ICustomListingVirtualTourState) => state.singleWithExtraData,
  },

  mutations: {
    ...base.mutations,
    setSingleWithExtraData: (state: ICustomListingVirtualTourState, payload: IListingVirtualToursWithExtraData) => {
      state.singleWithExtraData = payload;
    },
    resetSingleWithExtraData: (state: ICustomListingVirtualTourState) => {
      state.singleWithExtraData = null;
    },
  },

  actions: {
    ...base.actions,

    getWithTagsAndTranslations: async (
      ctx: IAction<IStateStrict<IVirtualTours>>,
      payload: IFilterPayloadV2<number>,
    ) => {
      await ctx.dispatch('get', {
        ...payload,
        ...{ additional: { includeTranslations: true } },
      } as IFilterPayloadV2<number>);

      const tagsResponse = await getByModelAndObjectId('virtual-tours,virtual_tours', ctx.getters.single.id);
      ctx.commit('setSingleWithExtraData', { ...ctx.getters.single, ...{ objectsHasTags: tagsResponse.data || [] } });
    },

    clearTagsAndTranslations: async (ctx: IAction<IStateStrict<IVirtualTours>>) => {
      ctx.commit('setSingleWithExtraData', null);
    },

    CreateNewVirtualTour: async (
      ctx: IAction<IStateStrict<IVirtualTours>>,
      payload: IModifyPayload<number, IVirtualTours>,
    ) => {
      const response: IApiResponse<IVirtualTours[], null> = await adminRequest.post(
        `/property-api/properties/${payload.parentId}/virtual-tours`,
        payload.data,
      );

      return response.data![0]!;
    },

    UpdateMany: async (
      ctx: IAction<IStateStrict<IVirtualTours>>,
      payload: IModifyPayload<number, IVirtualTours[]>,
    ) => {
      const response: IApiResponse<IVirtualTours[], null> = await adminRequest.put(
        `/property-api/properties/${payload.parentId}/virtual-tours`,
        payload.data,
      );

      return response.data;
    },

    DeleteVirtualTour: async (
      ctx: IAction<IStateStrict<IVirtualTours>>,
      payload: IModifyPayload<number, IVirtualTours>,
    ) => {
      const response: IApiCommandResponse<IVirtualTours[]> = await adminRequest.delete(
        `/property-api/properties/${payload.parentId}/virtual-tours`,
        [payload.data.id || -1],
      );

      if (!response) return false;

      return true;
    },

    saveUploadedThumbnailImageEdits: async (
      ctx: IAction<IStateStrict<IVirtualTours>>,
      payload: IModifyPayload<number, IVirtualTours, { clientKey: string, filename: string, imageEdits: IEditImage[] }>,
    ) => {
      const uploadEditResult = await uploads.updateImageAsNew(
        payload.additional!.clientKey,
        payload.additional!.filename,
        'gallery',
        payload.additional!.imageEdits,
      );

      payload.data.thumbnail = uploadEditResult.filename.split('/').pop();

      await ctx.dispatch('save', {
        id: payload.id!,
        data: {
          thumbnail: payload.data.thumbnail,
        },
      } as IFilterPayloadV2<number>);
    },
  },
};
